/**
 * Medical Record tab type filter — aligned with Settings → Documentation → Template Library tabs
 * (`TEMPLATE_CATEGORY_*` in `settings/template-library/types.ts`).
 *
 * Dropdown values are `TemplateCategory` codes; filtering uses the sublists of **record type strings**
 * produced by `getRecordTypeForItem` in `MedicalRecordTab.tsx`.
 */

import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  type TemplateCategory,
} from '../../settings/template-library/types';

/**
 * Granular record types under each template category (must cover `getRecordTypeForItem` outputs).
 */
export const MEDICAL_RECORD_CATEGORY_SUBLISTS: Record<TemplateCategory, readonly string[]> = {
  appointments: [
    'Screening Note',
    'Consultation Note',
    'Intake Note',
    'Progress Notes',
    'Case Review',
    'Crisis Session Note',
    'Psychotherapy Notes',
    'Family Therapy Progress Note',
    'Couples Therapy Progress Note',
    'Group Therapy Progress Note',
    'IOP Group Session Note',
  ],
  measures: ['Assessments', 'Medication Orders', 'Lab Orders'],
  'treatment-planning': [
    'Treatment Plan',
    'Safety Plan',
    'Risk Assessment',
    'Discharge Summary',
    'Transfer Request',
  ],
  forms: [
    'Intake Forms',
    'ROIs',
    'Consent Forms',
    'Legal Holds',
    'Court Documents',
    'Custody Agreements',
    'Good Faith Estimates',
  ],
  administrative: [
    'Inquiry Note',
    'Contact Attempts',
    'Collateral Contact Note',
    'Contact Logs',
    'Chart Notes',
    'Incident Reports',
    'Eligibility Notes',
    'Waitlist Entry',
    'Waitlist Activity',
    'Admin Notes',
    'Administrative Note',
    'Automated Emails',
  ],
};

/**
 * Legacy singular note types and the Medical Record filter value they normalize to.
 * `ClinicalNote.noteType` still carries both spellings; every consumer should normalize first.
 */
export const MEDICAL_RECORD_TYPE_ALIASES: Readonly<Record<string, string>> = {
  'Progress Note': 'Progress Notes',
  'Psychotherapy Note': 'Psychotherapy Notes',
  'Chart Note': 'Chart Notes',
};

/** Normalize a raw record type / note type to the canonical Medical Record filter value. */
export function normalizeMedicalRecordType(recordType: string | undefined): string | undefined {
  if (!recordType) return undefined;
  return MEDICAL_RECORD_TYPE_ALIASES[recordType] ?? recordType;
}

/**
 * Appointment/session documentation record types — the only records that may expand inline
 * in the chart timeline. Derived from the Appointments sublist (so it cannot drift) plus the
 * legacy singular aliases that normalize into it.
 */
export const SESSION_DOCUMENTATION_RECORD_TYPES: ReadonlySet<string> = new Set([
  ...MEDICAL_RECORD_CATEGORY_SUBLISTS.appointments,
  ...Object.keys(MEDICAL_RECORD_TYPE_ALIASES).filter((alias) =>
    MEDICAL_RECORD_CATEGORY_SUBLISTS.appointments.includes(MEDICAL_RECORD_TYPE_ALIASES[alias]),
  ),
]);

/** True when the record type is appointment/session documentation (Screening, Intake, Progress, …). */
export function isSessionDocumentationRecordType(recordType: string | undefined): boolean {
  const normalized = normalizeMedicalRecordType(recordType);
  return normalized !== undefined && SESSION_DOCUMENTATION_RECORD_TYPES.has(normalized);
}

/** Multiselect options: same five categories as Template Library. */
export const MEDICAL_RECORD_TYPE_OPTIONS: { value: TemplateCategory; label: string }[] =
  TEMPLATE_CATEGORIES.map((cat) => ({
    value: cat,
    label: TEMPLATE_CATEGORY_LABELS[cat],
  }));

/**
 * Given selected template category codes from the dropdown, return the set of record types to include.
 */
export function getRecordTypesForCategories(selectedCategories: string[]): Set<string> {
  if (selectedCategories.length === 0) return new Set();
  const set = new Set<string>();
  for (const cat of selectedCategories) {
    const sublist = MEDICAL_RECORD_CATEGORY_SUBLISTS[cat as TemplateCategory];
    if (sublist) sublist.forEach((t) => set.add(t));
  }
  return set;
}
